import { useState, ChangeEvent } from "react";
import { Alert, Dialog, Checkbox, RadioButtons, TextInput, Button } from "@narmi/design_system";
import { Case } from "./CaseTable";
import { Payment } from "./PaymentTable";
import { CSRF_TOKEN } from "@pages/csrf";

type InfoReqFormData = {
  kind: "missing" | "incorrect" | "duplicate" | undefined;
  aml: boolean;
  narrative: string | null;
};

function InformationRequestDialog({
  caseobj,
  payment,
  shown,
  setShown,
  successCallback,
}: {
  caseobj: Case | null;
  payment: Payment;
  shown: boolean;
  setShown: CallableFunction;
  successCallback: CallableFunction;
}) {
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [error, setError]: [string | null, CallableFunction] = useState(null);
  const [formData, setFormData]: [InfoReqFormData, CallableFunction] = useState({
    kind: undefined,
    aml: false,
    narrative: null,
  });

  async function onSubmit() {
    setDisableSubmit(true);
    const path = `/v1/fednow/payments/${payment.payment_identification}/information_request`;
    const bodyData: {
      kind: string | undefined;
      aml: boolean;
      narrative: string | null;
      case_id?: string | undefined;
    } = {
      kind: formData.kind,
      aml: formData.aml,
      narrative: formData.narrative === null ? "" : formData.narrative,
    };
    if (caseobj !== null && caseobj !== undefined) {
      bodyData.case_id = caseobj.case_identification;
    }
    const res = await fetch(path, {
      method: "POST",
      body: JSON.stringify(bodyData),
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": CSRF_TOKEN,
      },
    });
    const resData = await res.json();
    if (!res.ok) {
      if (typeof resData.message === "string") {
        setError(resData.message);
      } else {
        setError(Object.values(resData)[0]);
      }
      setDisableSubmit(false);
      return;
    }
    successCallback();
    setShown(false);
    setDisableSubmit(false);
  }

  return (
    <Dialog isOpen={shown} title="Request information" onUserDismiss={() => setShown(false)}>
      {error && (
        <div className="padding--bottom--s margin--bottom--s">
          <Alert isActive kind="error">
            {error}
          </Alert>
        </div>
      )}
      <div className="nds-typography margin--bottom--s padding--top--s">
        <p className="fontWeight--bold">Justification</p>
        <RadioButtons
          options={{
            "Missing information": "missing",
            "Incorrect information": "incorrect",
            "Possible duplicate instruction": "duplicate",
          }}
          name="justification"
          onChange={(e) => {
            const copy = { ...formData };
            copy.kind = e.target.value as "missing" | "incorrect" | "duplicate";
            // A "possible duplicate" request carries no narrative or AML flag
            // (there is no slot for them on the rail), so clear any stale values.
            if (copy.kind === "duplicate") {
              copy.narrative = null;
              copy.aml = false;
            }
            setDisableSubmit(
              (copy.kind === "missing" || copy.kind === "incorrect") &&
                (!copy.narrative || copy.narrative === "")
            );
            setFormData(copy);
          }}
        />
      </div>
      {(formData.kind === "missing" || formData.kind === "incorrect") && (
        <div className="padding--bottom--s padding--top--s margin--top--s">
          <p className="fontWeight--bold">Additional flags</p>
          <Checkbox
            label="Anti money-laundering request"
            name="aml"
            onChange={() => {
              const copy = { ...formData };
              copy.aml = !copy.aml;
              setFormData(copy);
            }}
          />
        </div>
      )}
      {(formData.kind === "missing" || formData.kind === "incorrect") && (
        <div className="padding--bottom--s margin--bottom--s padding--top--s">
          <div className="fontWeight--bold padding--bottom--s">Narrative</div>
          <TextInput
            label="Present a narrative here"
            error={
              !formData.narrative || formData.narrative === "" ? "Narrative is required" : null
            }
            value={formData.narrative ?? undefined}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const copy = { ...formData };
              copy.narrative = e.target.value;
              if (copy.narrative.length <= 140) {
                setDisableSubmit(!copy.narrative || copy.narrative === "");
                setFormData(copy);
              }
            }}
          />
          <div className="fontSize--xxs" style={{ float: "right" }}>
            {(formData.narrative || "").length}/140
          </div>
        </div>
      )}
      <div style={{ float: "right" }}>
        <Button label="Submit" onClick={onSubmit} disabled={disableSubmit} />
      </div>
    </Dialog>
  );
}

export default InformationRequestDialog;
